/* eslint-disable react-refresh/only-export-components */
import {
  DimeField,
  DimeFieldLabel,
  DimeInput,
  DimeLabel,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/label')({
  component: LabelDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-background p-6">
      <div className="mx-auto w-full max-w-xs">{children}</div>
    </div>
  );
}

function LabelDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Label</h1>
      <p className="mb-8 text-muted-foreground">
        Renders an accessible label associated with a form control. Pair with{' '}
        <code className="font-mono text-sm">htmlFor</code> and a matching control{' '}
        <code className="font-mono text-sm">id</code>.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <div className="flex flex-col gap-2">
            <DimeLabel htmlFor="label-basic">Email</DimeLabel>
            <DimeInput id="label-basic" type="email" placeholder="m@example.com" />
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">htmlFor</code> on{' '}
          <code className="font-mono">DimeLabel</code> and a matching{' '}
          <code className="font-mono">id</code> on the control so clicking the label focuses the
          input.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <div className="group flex flex-col gap-2" data-disabled>
            <DimeLabel htmlFor="label-disabled">Locked email</DimeLabel>
            <DimeInput
              id="label-disabled"
              className="peer"
              disabled
              placeholder="locked@example.com"
            />
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Add <code className="font-mono">data-disabled</code> to a parent with{' '}
          <code className="font-mono">group</code>, or add <code className="font-mono">peer</code>{' '}
          to a disabled control, to dim the label. For full field layouts see{' '}
          <a href="/design-system/field" className="underline">
            Field
          </a>
          .
        </p>
      </DocsSection>

      <DocsSection title="Required">
        <Preview>
          <div className="flex flex-col gap-2">
            <DimeLabel htmlFor="label-required">
              Full name <span className="text-destructive">*</span>
            </DimeLabel>
            <DimeInput id="label-required" placeholder="Test User" required />
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeLabel</code> has no{' '}
          <code className="font-mono">required</code> prop. For simple label+control pairs, add a
          visual indicator in children — a red asterisk is the common convention — and set{' '}
          <code className="font-mono">required</code> on the control for native validation.
        </p>
      </DocsSection>

      <DocsSection title="With Field">
        <Preview>
          <DimeField className="w-full">
            <DimeFieldLabel htmlFor="label-field">Work email</DimeFieldLabel>
            <DimeInput id="label-field" type="email" placeholder="you@company.com" />
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          For descriptions, errors, groups, and other structured form layouts, use{' '}
          <code className="font-mono">DimeField</code> with{' '}
          <code className="font-mono">DimeFieldLabel</code> instead of{' '}
          <code className="font-mono">DimeLabel</code>. See{' '}
          <a href="/design-system/field" className="underline">
            Field
          </a>{' '}
          for full form layouts.
        </p>
      </DocsSection>
    </div>
  );
}
